// @ds/forms/SchemaFormItem — the per-field form renderer.
//
// Takes ONE schema field (ListColumnSpec produced by `defineSchema<T>()`) and
// emits a Form.Item with the right widget for its `edit.field.type`. The
// widget vocabulary mirrors `EditableFieldType` from `@ds/editable/Editable`
// 1:1 — same discriminant, same options shape — so the form-context input
// and the inline-edit input render the same control for the same field
// type.
//
// What it reads from the schema:
//   - labelKey       → Form.Item label (t()-resolved)
//   - edit.key       → Form.Item name (mutation column on T)
//   - edit.field     → widget type (resolved if a function — see Phase 7
//                      record-aware dispatch)
//   - edit.required  → Form.Item rules
//   - edit.validate  → Form.Item rules (sync validator)
//   - edit.placeholder / edit.helpText → label aux text
//
// What the caller controls (per-call overrides):
//   - widget         → replace the dispatcher's choice with a custom widget
//   - placeholder    → override
//   - helpText       → override
//   - autoFocus      → on/off
//   - disabled       → on/off (the orchestrator can pass values-based flags)
//   - dependencies / extraRules → cross-field validation (e.g. an end date
//                      that must not precede a start date); schema rules
//                      still apply first
//
// Authors can also use SchemaFormItem outside the SchemaForm orchestrator —
// drop it into any hand-written `<Form>` for incremental wins. The
// orchestrator just wires sections + parent FK + submit; this primitive
// is self-sufficient.

import React from "react";
import type { ReactNode } from "react";
import { Form, Input } from "antd";
import type { FormRule } from "antd";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import type { ListColumnSpec } from "@ds/tables/types";
import type { ErasedTypeArg } from "@ds/display/fieldDisplay";
import type { EditableFieldType } from "@ds/editable/Editable";
import {
  renderFieldWidget,
  resolveOptionLabels,
} from "@ds/editable/fieldTypeRegistry";
import { useFormFieldTestIdPrefix } from "@ds/forms/formTestIdContext";

// ─── Inputs ──────────────────────────────────────────────────────────────

export interface SchemaFormItemInput<T> {
  /** The schema field — typically `companySchema.nip`. */
  field: ListColumnSpec<T, ErasedTypeArg>;
  /** Replace the dispatcher's widget choice. The widget receives the
   *  AntD-form value/onChange contract. */
  widget?: React.ComponentType<{
    value?: unknown;
    onChange?: (v: unknown) => void;
    disabled?: boolean;
    placeholder?: string;
    autoFocus?: boolean;
  }>;
  /** Override placeholder. Defaults to `edit.placeholder` if declared, else
   *  the empty string (AntD treats no placeholder as fine). */
  placeholder?: string;
  /** Help text rendered below the input via Form.Item `extra`. */
  helpText?: ReactNode;
  /** Autofocus this field. */
  autoFocus?: boolean;
  /** Disable the input. */
  disabled?: boolean;
  /** Hidden field — renders an invisible Form.Item carrying the value. */
  hidden?: boolean;
  /**
   * Per-call required override. When set, wins over `schema.edit.required`.
   * Use for form-context-specific requirements (e.g. a field that's
   * required when creating but optional when editing).
   */
  required?: boolean;
  /**
   * Per-call extraOption — only meaningful for `entity-select` field
   * types. When the form has hydrated a record with joined-relation
   * data, the caller pre-resolves the display label for the currently-
   * stored id and passes it here. The widget renders it as an extra
   * option so the Select shows "Acme Sp. z o.o." instead of the bare
   * id while the list query loads. Used by edit-mode entity selects
   * with joined hydrate queries (e.g. BasicDetailsModal's 11 entity-
   * select fields).
   */
  extraOption?: { value: number | string; label: ReactNode } | null;
  /**
   * Fields whose change re-validates this one (AntD `Form.Item dependencies`).
   * Pair with `extraRules` for cross-field checks the single-value
   * `edit.validate` cannot express.
   */
  dependencies?: string[];
  /**
   * Extra AntD rules appended after the schema-derived ones (required,
   * `edit.validate`). Function rules receive the form instance, so they can
   * read sibling fields: `({ getFieldValue }) => ({ validator })`.
   */
  extraRules?: FormRule[];
}

// SchemaFormItem owns no events / actions — Form.Item wires those.

export type SchemaFormItemProps<T> = SchemaFormItemInput<T>;

// ─── Component ───────────────────────────────────────────────────────────

export function SchemaFormItem<T>({
  field,
  widget,
  placeholder,
  helpText,
  autoFocus,
  disabled,
  hidden,
  required,
  extraOption,
  dependencies,
  extraRules,
}: SchemaFormItemProps<T>) {
  const { t } = useTranslation();
  const testIdPrefix = useFormFieldTestIdPrefix();
  const edit = field.edit;

  // Hidden fields render as a hidden Form.Item — drives values that the
  // submit needs (parent FK, generated metadata) without UI weight.
  if (hidden) {
    return (
      <Form.Item name={edit?.key ?? field.key} hidden>
        <Input type="hidden" />
      </Form.Item>
    );
  }

  // No edit metadata → schema field is display-only. Render nothing rather
  // than throwing, so a missing `edit:` is a visible-in-DOM no-op (easier
  // to diagnose in dev than a hard crash).
  if (!edit) {
    Sentry.captureMessage(
      `[SchemaFormItem] field "${field.key}" has no edit metadata; skipping.`,
      "warning",
    );
    return null;
  }

  // Resolve the edit.field discriminant. In form context (especially
  // create) we don't have a record yet, so the function form is called
  // with `null` cast to T — schemas that use the function form (e.g.
  // entity-select with per-record displayLabel) handle null on their own.
  const editFieldType: EditableFieldType =
    typeof edit.field === "function"
      ? ((edit.field as (r: unknown) => EditableFieldType)(null as unknown))
      : ((edit.field) ?? { type: "text" });

  // Required: per-call override wins, then schema.edit.required (function
  // form resolves against null since form-context has no record yet).
  const isRequired =
    required !== undefined
      ? required
      : typeof edit.required === "function"
        ? ((edit.required as (r: unknown) => boolean)(null as unknown))
        : !!edit.required;

  const labelText = field.labelKey ? t(field.labelKey) : field.label ?? edit.key;

  const rules: FormRule[] = [];
  if (isRequired) {
    rules.push({ required: true, message: t("common.validation.required") });
  }
  const validate = edit.validate;
  if (validate) {
    rules.push({
      validator: async (_rule: unknown, value: unknown) => {
        const msg = validate(value);
        if (msg) throw new Error(msg);
      },
    });
  }
  if (extraRules) rules.push(...extraRules);

  // Resolve placeholder. Schemas typically store an i18n key (e.g.
  // "products.placeholders.name") in `edit.field.placeholder`. We `t()` it
  // with the key itself as the defaultValue — i18next returns the
  // translated string if the key exists, otherwise the defaultValue (so raw
  // Polish strings pass through unchanged). Per-call `placeholder` prop
  // wins over the schema's value.
  const rawPlaceholder = placeholder ?? extractPlaceholder(editFieldType);
  const finalPlaceholder = rawPlaceholder
    ? t(rawPlaceholder, rawPlaceholder)
    : undefined;
  const testId =
    testIdPrefix !== "modal-form-field"
      ? `${testIdPrefix}-${edit.key}`
      : undefined;

  // Resolve option labels for select / multiselect. Same i18n-key-with-
  // fallback contract as placeholders — option `label` is an i18n key,
  // resolved via `t(label, label)`; raw strings pass through.
  const resolvedFieldType = resolveOptionLabels(
    editFieldType,
    (k: string) => t(k, k) as unknown as string,
  );

  // Render the widget — caller override or shared registry.
  // The registry is the single source of truth used by both this form
  // context and the inline-edit `<Editable>` context, so a field declared
  // once on the schema renders identically everywhere.
  const widgetNode = widget
    ? React.createElement(widget, {
        disabled,
        placeholder: finalPlaceholder,
        autoFocus,
        ...(testId ? { "data-testid": testId } : {}),
      } as React.ComponentProps<typeof widget>)
    : renderFieldWidget(resolvedFieldType, {
        disabled,
        placeholder: finalPlaceholder,
        autoFocus,
        testId,
        ...(extraOption !== undefined ? { extraOption } : {}),
        // Form context leaves value/onChange/onKeyDown/onBlur unset —
        // Form.Item injects value+onChange via cloneElement.
      });

  // Value sentinel mapping (radio-group with `valueMap`). Form value ↔ UI
  // value bridge runs at the Form.Item level: `getValueProps` maps the
  // stored value (e.g. `null`) to the UI value (e.g. `"both"`); `normalize`
  // does the inverse on every change so the form/payload sees the dbValue.
  const valueMap =
    editFieldType.type === "radio-group"
      ? (editFieldType as { valueMap?: { uiSentinel: string; dbValue: unknown } }).valueMap
      : undefined;

  // A cleared single entity-select emits `undefined`, which drops out of the
  // JSON PATCH body — store `null` so the cleared FK actually reaches the DB.
  const clearsToNull =
    editFieldType.type === "entity-select" &&
    (editFieldType as { mode?: string }).mode !== "multiple";

  const errorTestId = testId ? `${testId}-error` : undefined;

  return (
    <>
      <Form.Item
        name={edit.key}
        label={labelText}
        rules={rules}
        {...(dependencies ? { dependencies } : {})}
        extra={helpText}
        valuePropName={valuePropName(editFieldType)}
        {...(valueMap
          ? {
              getValueProps: (v: unknown) => ({
                value: v === valueMap.dbValue ? valueMap.uiSentinel : v,
              }),
              normalize: (v: unknown) =>
                v === valueMap.uiSentinel ? valueMap.dbValue : v,
            }
          : {})}
        {...(clearsToNull ? { normalize: (v: unknown) => v ?? null } : {})}
      >
        {widgetNode}
      </Form.Item>
      {/* Additive testid hook for the per-field validation message. AntD's
          native explain (visible, styled, animated) is left untouched above;
          this marker mirrors the current error text under a deterministic
          `{prefix}-{key}-error` testid — matching ModalForm's convention —
          so e2e can address it without `.ant-*` selectors. Screen-reader-only
          (1×1, clipped) so it never visually duplicates the native message.
          `shouldUpdate` keeps it in sync with async validator settling. */}
      {errorTestId && (
        <Form.Item noStyle shouldUpdate>
          {(form) => {
            const errors = form.getFieldError(edit.key);
            return errors.length > 0 ? (
              <span data-testid={errorTestId} className="ds-sr-only">
                {errors.join(" ")}
              </span>
            ) : null;
          }}
        </Form.Item>
      )}
    </>
  );
}

// ─── AntD control-contract helpers ──────────────────────────────────────

/** AntD Form.Item passes `value` by default; some controls need `checked`. */
function valuePropName(field: EditableFieldType): string {
  return field.type === "boolean" ? "checked" : "value";
}

/** Lift the per-type placeholder from the field options when present. */
function extractPlaceholder(field: EditableFieldType): string | undefined {
  if ("placeholder" in field && typeof field.placeholder === "string") {
    return field.placeholder;
  }
  return undefined;
}

